/**
 * Route classification for auth-related side effects.
 *
 * Layered defense against the "session expired" toast/redirect firing on public pages
 * (card views, marketing pages, SEO articles, blog, legal). Used by:
 *   - useAutoTokenRefresh: to skip proactive token refresh when the user is on a public
 *     page (a QR-scanning visitor doesn't need their token maintained while browsing).
 *   - useAuth.handleTokenExpired: to skip the toast + redirect-to-/login when a token
 *     expires event fires while the user is on a public page.
 *   - api.request 401 handler: same suppression on public pages.
 *
 * Design: default to public unless the path matches an AUTH_REQUIRED pattern. This is
 * safer than the inverse (a new public route silently breaking) because the app has
 * ~200 public content routes vs. ~20 auth-required ones.
 *
 * Adding a new auth-required route? Add its pattern below.
 */

// Match rules — order doesn't matter, first hit wins.
const AUTH_REQUIRED_PATTERNS: RegExp[] = [
  /^\/dashboard(\/|$)/,
  /^\/card$/,                              // /card = editor (auth). /card/:slug = public view.
  /^\/qr-designer$/,                        // /qr-designer-visitor is a separate public tool.
  /^\/qr-store(\/|$)/,
  /^\/analytics(\/|$)/,
  /^\/wallet(\/|$)/,
  /^\/qrshare(\/|$)/,
  /^\/virtual-background(\/|$)/,
  /^\/forms(\/|$)/,                         // /qr-form/... is the public visitor form.
  /^\/leads(\/|$)/,
  /^\/crm-integrations(\/|$)/,
  /^\/team(\/|$)/,
  /^\/settings(\/|$)/,
  /^\/appearance(\/|$)/,
  /^\/email(\/|$)/,
  /^\/email-signature(\/|$)/,
  /^\/ai-notes(\/|$)/,
  /^\/admin(\/|$)/,
  /^\/corporate\/dashboard/,
  /^\/corporate\/onboarding/,
  /^\/enterprise\/pending-approval/,
  /^\/events$/,                             // /events = list (auth).
  /^\/events\/new/,                         // create wizard (auth).
  /^\/events\/[^/]+\/dashboard/,            // organizer dashboard. /events/badge/:token is public.
];

export function isPublicRoute(pathname: string = window.location.pathname): boolean {
  return !AUTH_REQUIRED_PATTERNS.some((re) => re.test(pathname));
}

export function isAuthRequiredRoute(pathname: string = window.location.pathname): boolean {
  return !isPublicRoute(pathname);
}
